import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Trash2, UserPlus } from 'lucide-react';
import workspaceService from '@/services/workspace.service';
import type { GroupPerson, LayerGroup } from '@/services/layerGroups.service';

/**
 * Who my menu is shared with: the whole company, and/or chosen colleagues (the user management service's list of
 * the company's people, the same one workspaces are shared from). Whoever gets it sees only the layers they may see
 * anyway, and cannot change it.
 */
export const ShareGroupPanel: React.FC<{
  group: LayerGroup;
  saving: boolean;
  onSave: (company: boolean, people: GroupPerson[]) => void;
  onClose: () => void;
}> = ({ group, saving, onSave, onClose }) => {
  const { t } = useTranslation();
  const [company, setCompany] = useState(!!group.sharing?.company);
  const [people, setPeople] = useState<GroupPerson[]>(group.sharing?.people ?? []);
  const [who, setWho] = useState('');
  const { data: colleagues = [], isError } = useQuery({ queryKey: ['colleagues'], queryFn: () => workspaceService.colleagues(), retry: false });
  const choosable = colleagues.filter((c) => c.principalId !== group.owner && !people.some((p) => p.subject === c.principalId));

  const add = () => {
    const c = colleagues.find((x) => x.principalId === who);
    if (!c) return;
    setPeople([...people, { subject: c.principalId, name: c.displayName }]);
    setWho('');
  };

  return (
    <div className="mx-1 mb-1 p-2 border border-gn-light rounded text-xs space-y-1.5 bg-gn-secondary" data-testid="share-panel">
      <p className="text-[10px] text-gn-muted">{t('layerGroups.share_hint')}</p>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={company} onChange={(e) => setCompany(e.target.checked)} />
        <Building2 size={12} className="text-gn-secondary" />
        <span>{t('layerGroups.share_company')}</span>
      </label>
      <div className="space-y-0.5">
        <p className="text-[10px] uppercase tracking-wide text-gn-muted">{t('layerGroups.share_people')}</p>
        {people.length === 0 && <p className="text-[10px] text-gn-muted italic">{t('layerGroups.share_nobody')}</p>}
        {people.map((p) => (
          <div key={p.subject} className="flex items-center gap-1">
            <span className="flex-1 truncate">{p.name || colleagues.find((c) => c.principalId === p.subject)?.displayName || p.subject}</span>
            <button type="button" className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary" title={t('layerGroups.unshare')}
                    onClick={() => setPeople(people.filter((x) => x.subject !== p.subject))}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <div className="flex gap-1">
          <select className="flex-1 min-w-0 px-2 py-1 border border-gn-light rounded bg-gn-surface text-gn-primary text-xs" value={who}
                  onChange={(e) => setWho(e.target.value)} aria-label={t('layerGroups.share_pick')}>
            <option value="">{t('layerGroups.share_pick')}</option>
            {choosable.map((c) => <option key={c.principalId} value={c.principalId}>{c.displayName}</option>)}
          </select>
          <button type="button" className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary disabled:opacity-40" disabled={!who} onClick={add}
                  title={t('layerGroups.share_add')}>
            <UserPlus size={13} />
          </button>
        </div>
        {isError && <p className="text-[10px] text-gn-muted italic">{t('layerGroups.share_no_people')}</p>}
      </div>
      <div className="flex justify-end gap-1 pt-1">
        <button type="button" className="px-2 py-1 rounded hover:bg-gn-tertiary" onClick={onClose}>{t('layerGroups.cancel')}</button>
        <button type="button" className="px-2 py-1 rounded bg-gn-blue text-gn-inverse disabled:opacity-50" disabled={saving}
                onClick={() => onSave(company, people)}>
          {t('layerGroups.save')}
        </button>
      </div>
    </div>
  );
};

export default ShareGroupPanel;
